/* Shop strips (snippets/shop-strip.liquid): rows of product cards inside blog posts and pages.

   Collection and hand-picked strips arrive built (the site's own card). Live strips (designer,
   category, product type, tag) are filled here from the LDJ search app, with the rules the
   site's cards follow (snippets/product-card.liquid, assets/search-overlay.js):
   - prices in the shopper's currency, rounded the way the market rounds (window.LdjMoneyRule):
     the service stores the shop's own currency;
   - no sold pieces: the service is asked for pieces in stock, and a Consignr piece whose
     layaway is paid is left out, as the cards treat it as sold;
   - the house spellings (Hermès, Chloé).
   A strip with nothing to show, or whose request fails, hides.

   Links carry no tracking parameters (they would re-credit the visit to the blog): a click is
   published as a Shopify customer event, ldj_shop_strip_click, for an analytics pixel. */
(() => {
  if (window.LdjShopStrip) return;

  const HOUSE_NAMES = { hermes: 'Hermès', chloe: 'Chloé' };
  const fold = (value) => String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  const houseName = (name) => HOUSE_NAMES[fold(name)] || name;
  const esc = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Prices in the shopper's currency, as search-overlay.js writes them.
  const currency = () => ({
    active: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD',
    rate: Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1
  });
  const formats = new Map();
  const formatPrice = (amount) => {
    const { active, rate } = currency();
    const rule = rate === 1 ? 'cents' : (window.LdjMoneyRule && window.LdjMoneyRule.get()) || 'whole';
    const converted = Number(amount) * rate;
    const value = rule === 'cents' ? Math.round(converted * 100) / 100 : Math.round(converted);
    if (!Number.isFinite(value) || value <= 0) return '';
    // Cents only when there are some ("$2,625", never "$2,625.00").
    const digits = rule === 'cents' && value % 1 !== 0 ? 2 : 0;
    const key = `${active}:${digits}`;
    if (!formats.has(key)) {
      let format;
      try {
        format = new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency', currency: active, currencyDisplay: 'narrowSymbol', minimumFractionDigits: digits, maximumFractionDigits: digits
        });
      } catch (_error) {
        format = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: digits, maximumFractionDigits: digits });
      }
      formats.set(key, format);
    }
    return formats.get(key).format(value);
  };

  // Shopify's CDN resizes on request: ask for the size a photo is drawn at.
  const sized = (url, width) => {
    if (!url) return '';
    try {
      const next = new URL(url, window.location.origin);
      if (!/cdn\.shopify\.com|\/cdn\/shop\//.test(next.href)) return url;
      next.searchParams.set('width', String(width));
      return next.toString();
    } catch (_error) {
      return url;
    }
  };
  const srcset = (url) => [360, 540, 720, 960].map((width) => `${sized(url, width)} ${width}w`).join(', ');

  const photos = (hit) => {
    const seen = new Set();
    return [hit.image, ...(Array.isArray(hit.images) ? hit.images : [])]
      .map((candidate) => (typeof candidate === 'string' ? candidate : (candidate && (candidate.src || candidate.url)) || ''))
      .filter((src) => src && !seen.has(src) && seen.add(src));
  };

  const isSold = (hit) => {
    if (hit.availability === 'out_of_stock') return true;
    const tags = (Array.isArray(hit.tags) ? hit.tags : []).map(fold);
    return tags.includes('consignr') && tags.includes('layaways-paid');
  };

  const card = (hit, sizes) => {
    const title = String(hit.title || '').trim();
    const designerRaw = String(hit.designer || hit.brand || hit.vendor || '').trim();
    let display = title;
    if (designerRaw && title.toLowerCase().startsWith(designerRaw.toLowerCase())) {
      display = title.slice(designerRaw.length).trim().replace(/^[-–]\s*/, '');
    }
    const designer = houseName(designerRaw);
    const [image, secondary] = photos(hit);
    const base = Number(hit.price_min ?? hit.price);
    const price = formatPrice(base);
    const condition = String(hit.condition || '').trim();
    const url = hit.url || `/products/${encodeURIComponent(hit.handle || '')}`;
    const picture = (src, primary) => `<img class="product-card__image product-card__image--${primary ? 'primary' : 'secondary'}" src="${esc(sized(src, 720))}" srcset="${esc(srcset(src))}" sizes="${esc(sizes)}" alt="${primary ? esc(hit.image_alt || title) : ''}" loading="lazy" decoding="async">`;
    return `
      <article class="product-card card-wrapper product-card-wrapper luxe-product-card${secondary ? ' product-card--has-secondary' : ''}" data-product-card data-product-handle="${esc(hit.handle)}">
        <div class="product-card__frame card card--standard card--media">
          <a class="product-card__media" href="${esc(url)}" tabindex="-1" aria-hidden="true">
            ${image ? `<span class="product-card__image-wrapper fp-image-wrapper">${picture(image, true)}${secondary ? picture(secondary, false) : ''}</span>` : ''}
          </a>
        </div>
        <div class="product-card__content card__content underline-links-hover">
          <div class="product-card__details ais-hit--details card__information">
            <h3 class="product-card__title fp-title card__heading">
              ${designer ? `<span class="product-card__vendor fp-card__vendor" translate="no">${esc(designer)}</span>` : ''}
              <a class="product-card__link full-unstyled-link fp-card__link" href="${esc(url)}"><span class="fp-card__link__product-name">${esc(display || title)}</span></a>
            </h3>
            ${condition ? `<p class="product-card__condition fp-condition left"><span class="product-card__condition-label product-card__condition-label--mobile">Cond: </span><span class="product-card__condition-label product-card__condition-label--desktop">Condition: </span>${esc(condition)}</p>` : '<p class="product-card__condition product-card__condition--empty" aria-hidden="true">&nbsp;</p>'}
            ${price ? `<div class="product-card__price price" data-ldj-strip-base="${esc(base)}">${esc(price)}</div>` : ''}
          </div>
        </div>
      </article>`;
  };

  // The rail scrolls sideways: the scrollbar, the cut card at the edge and this pair of arrows
  // are what say so. Returns the function that re-reads the arrows' state.
  const rails = new WeakMap();
  const setupRail = (strip) => {
    if (!strip.classList.contains('ldj-shop-strip--rail')) return null;
    if (rails.has(strip)) return rails.get(strip);
    const grid = strip.querySelector('[data-ldj-shop-strip-grid]');
    if (!grid) return null;
    const nav = document.createElement('div');
    nav.className = 'ldj-shop-strip__rail-nav';
    nav.innerHTML = '<button type="button" class="ldj-shop-strip__rail-btn" data-rail-prev aria-label="Previous"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M15 5l-7 7 7 7"/></svg></button><button type="button" class="ldj-shop-strip__rail-btn" data-rail-next aria-label="Next"><svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 5l7 7-7 7"/></svg></button>';
    grid.insertAdjacentElement('afterend', nav);
    const prev = nav.querySelector('[data-rail-prev]');
    const next = nav.querySelector('[data-rail-next]');
    const step = () => {
      const tile = grid.querySelector('.product-card, .ldj-shop-strip__skeleton');
      return tile ? tile.getBoundingClientRect().width + 16 : grid.clientWidth * 0.4;
    };
    const sync = () => {
      const max = grid.scrollWidth - grid.clientWidth;
      prev.disabled = grid.scrollLeft <= 4;
      next.disabled = grid.scrollLeft >= max - 4;
      nav.hidden = max <= 4;
    };
    const go = (direction) => grid.scrollBy({ left: direction * step(), behavior: reducedMotion() ? 'auto' : 'smooth' });
    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    grid.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
    rails.set(strip, sync);
    return sync;
  };

  const repriceAll = () => {
    document.querySelectorAll('[data-ldj-strip-base]').forEach((element) => {
      const price = formatPrice(element.dataset.ldjStripBase);
      if (price) element.textContent = price;
    });
  };

  const hide = (strip) => {
    strip.hidden = true;
  };

  const hydrate = async (strip) => {
    if (strip.dataset.ldjShopStripLoaded) return;
    strip.dataset.ldjShopStripLoaded = '1';
    const grid = strip.querySelector('[data-ldj-shop-strip-grid]');
    const endpoint = strip.dataset.endpoint;
    const value = strip.dataset.value || '';
    if (!grid || !endpoint || !value) return hide(strip);

    const limit = Math.min(Math.max(Number(strip.dataset.limit) || 5, 1), 12);
    const isTag = strip.dataset.param === 'tag';
    const url = new URL(endpoint, window.location.origin);
    // A tag rides the full text; only the pieces that carry it stay. Ask for a few more than
    // needed so that the pieces left out still leave a full row.
    url.searchParams.set('q', isTag ? value : '*');
    url.searchParams.set('per_page', String(isTag ? 48 : limit + 6));
    url.searchParams.set('availability', 'in_stock');
    // Not a shopper's search: the service doesn't record it (as the other non-search uses).
    url.searchParams.set('analytics', 'false');
    if (!isTag) url.searchParams.set(strip.dataset.param || 'designer', value);

    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' }, credentials: 'omit' });
      if (!response.ok) throw new Error(String(response.status));
      const payload = await response.json();
      const wanted = fold(value);
      const hits = (Array.isArray(payload.hits) ? payload.hits : [])
        .filter((hit) => !isSold(hit))
        .filter((hit) => !isTag || (Array.isArray(hit.tags) && hit.tags.some((tag) => fold(tag) === wanted)))
        .slice(0, limit);
      if (!hits.length) return hide(strip);
      grid.innerHTML = hits.map((hit) => card(hit, strip.dataset.cardSizes || '50vw')).join('');
      grid.removeAttribute('aria-busy');
      const sync = setupRail(strip);
      if (sync) sync();
      // The first live cards ask Shopify how this market rounds converted prices (once per
      // session, see layout/theme.liquid), then every strip's prices are written again.
      if (window.LdjMoneyRule && !window.LdjMoneyRule.get()) {
        window.LdjMoneyRule.probe(hits.map((hit) => ({ handle: hit.handle, base: Number(hit.price_min ?? hit.price) })), repriceAll);
      }
    } catch (_error) {
      hide(strip);
    }
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        hydrate(entry.target);
      });
    }, { rootMargin: '400px 0px' })
    : null;

  const init = (root) => {
    root.querySelectorAll('[data-ldj-shop-strip]').forEach((strip) => {
      if (strip.dataset.ldjShopStripReady) return;
      strip.dataset.ldjShopStripReady = '1';
      setupRail(strip);
      if (!strip.hasAttribute('data-ldj-shop-strip-dynamic')) return;
      if (observer) observer.observe(strip);
      else hydrate(strip);
    });
  };

  // A long jump (an anchor, a restored scroll position) can pass a strip before the observer
  // sees it in any frame: catch up once the page has settled.
  window.addEventListener('load', () => {
    window.setTimeout(() => {
      document.querySelectorAll('[data-ldj-shop-strip-dynamic]').forEach((strip) => {
        if (!strip.dataset.ldjShopStripLoaded && strip.getBoundingClientRect().top < window.innerHeight) hydrate(strip);
      });
    }, 600);
  });

  // Clicks: a customer event for an analytics pixel (where the strip sits, what was opened).
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('[data-ldj-shop-strip] a[href]');
    if (!link) return;
    const strip = link.closest('[data-ldj-shop-strip]');
    const card = link.closest('[data-product-card]');
    const cards = Array.from(strip.querySelectorAll('[data-product-card]'));
    try {
      window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish('ldj_shop_strip_click', {
        campaign: strip.dataset.trackCampaign || '',
        content: strip.dataset.trackContent || '',
        source: strip.dataset.source || '',
        product: card ? card.dataset.productHandle || '' : '',
        position: card ? cards.indexOf(card) + 1 : 0,
        button: link.hasAttribute('data-ldj-shop-strip-cta'),
        url: link.getAttribute('href')
      });
    } catch (_error) {}
  });

  // The theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  init(document);
  window.LdjShopStrip = { init };
})();
